// The entity-level base types live in `dxfPrimitives.ts` — a leaf with no
// imports — so LEAF type modules (`types.legacyPolyline.ts`,
// `types.tableOle.ts`, `objects/objectsAnnotativeTypes.ts`) can build on them
// without back-importing this document-level module and closing a
// `no-circular` cycle. Re-exported here so every existing `from "./types"`
// import path keeps working; see that module's header for the full rationale.
export {
  type DxfGroup,
  type DxfPoint3D,
  type DxfSpaceKind,
  type DxfCommonEntityProps,
  type DxfSupportedEntityType,
  type DxfXDataEntry,
  type DxfSupportedEntityBase,
  type DxfSeqendEntity,
} from "./dxfPrimitives";

// Only the names this module itself REFERENCES — a re-export above does not
// bring a symbol into this file's scope, and importing one it never uses is a
// lint error waiting to happen.
import type {
  DxfGroup,
  DxfPoint3D,
  DxfSupportedEntityType,
  DxfXDataEntry,
  DxfSupportedEntityBase,
  DxfSeqendEntity,
} from "./dxfPrimitives";


export type DxfLineEntity = DxfSupportedEntityBase & {
  type: "LINE";
  geometry: {
    start: DxfPoint3D;
    end: DxfPoint3D;
  };
};

// AutoCAD POINT entity (AcDbPoint). Group codes:
//   10/20/30 — location of the point (x, y, z)
//   39       — thickness (optional, on common props)
//   210/220/230 — extrusion direction (OCS), default (0,0,1)
//   50       — angle of X axis for the UCS in effect when the point was
//              drawn (radians); used by PDMODE marker shapes (like X, +)
//              that are oriented relative to that axis. Default 0.
// Rendered shape is determined by the system variable PDMODE (0 = single
// dot, 2 = +, 3 = X, 4 = vertical tick, 32+ adds circle, 64+ adds square)
// at draw time; the entity itself stores only location + rotation.
export type DxfPointEntity = DxfSupportedEntityBase & {
  type: "POINT";
  geometry: {
    location: DxfPoint3D;
    rotation?: number;
  };
};

export type DxfCircleEntity = DxfSupportedEntityBase & {
  type: "CIRCLE";
  geometry: {
    center: DxfPoint3D;
    radius: number;
  };
};

export type DxfArcEntity = DxfSupportedEntityBase & {
  type: "ARC";
  geometry: {
    center: DxfPoint3D;
    radius: number;
    startAngle: number;
    endAngle: number;
  };
};

// AutoCAD ELLIPSE entity (AcDbEllipse). Group codes:
//   10/20/30 — center point
//   11/21/31 — major axis end-point (vector relative to center)
//   210/220/230 — extrusion direction (OCS), default (0,0,1)
//   40 — minor-to-major ratio (0..1]
//   41 — start parameter (radians, 0 for full ellipse)
//   42 — end parameter (radians, 2π for full ellipse)
export type DxfEllipseEntity = DxfSupportedEntityBase & {
  type: "ELLIPSE";
  geometry: {
    center: DxfPoint3D;
    majorAxisEndpoint: DxfPoint3D;
    ratio: number;
    startParam: number;
    endParam: number;
  };
};

// --- SPLINE ---
// AutoCAD SPLINE entity (AcDbSpline). Full NURBS plus fit-points mode.
// Group codes:
//   100 AcDbSpline subclass marker
//    70 flags bitfield (1=closed, 2=periodic, 4=rational, 8=planar,
//                       16=linear)
//    71 degree (typically 3 = cubic)
//    72 number of knots
//    73 number of control points
//    74 number of fit points
//    42 knot tolerance (default 1e-7)
//    43 control-point tolerance (default 1e-10)
//    44 fit tolerance (default 1e-10)
//   12/22/32 start tangent (relative to first fit point)
//   13/23/33 end tangent
//    40 (multiple) knot value
//    41 (multiple) per-control-point weight (default 1.0)
//   10/20/30 (multiple) control point
//   11/21/31 (multiple) fit point
export type DxfSplineEntity = DxfSupportedEntityBase & {
  type: "SPLINE";
  geometry: {
    degree: number;
    flags: number;
    closed: boolean;
    periodic: boolean;
    rational: boolean;
    planar: boolean;
    linear: boolean;
    knots: number[];
    controlPoints: DxfPoint3D[];
    weights: number[];
    fitPoints: DxfPoint3D[];
    startTangent?: DxfPoint3D;
    endTangent?: DxfPoint3D;
    knotTolerance?: number;
    controlPointTolerance?: number;
    fitTolerance?: number;
  };
};

// --- LWPOLYLINE ---

export type DxfLwPolylineVertex = {
  x: number;
  y: number;
  bulge?: number;
  startWidth?: number;
  endWidth?: number;
  // DXF group 91 — per-vertex persistent identifier, interleaved with the
  // 10/20/42 codes inside AcDbPolyline body. AutoCAD assigns a unique
  // integer to every vertex at creation time (or xdata-load time for
  // constraint-based blocks). The ID is the subent-ID that
  // ACDBASSOCGEOMDEPENDENCY records reference when pointing at a specific
  // edge or vertex on this polyline. Undefined on polylines without
  // persistent-reactor tagging (legacy fixtures, purely-static blocks).
  vertexId?: number;
};

export type DxfLwPolylineEntity = DxfSupportedEntityBase & {
  type: "LWPOLYLINE";
  geometry: {
    vertices: DxfLwPolylineVertex[];
    closed: boolean;
    // DXF group 70 bit 128 — plinegen (linetype generated continuously
    // across the whole polyline rather than restarting at each vertex).
    plinegen?: boolean;
    constantWidth?: number;
    elevation?: number;
    vertexCount: number;
  };
};

// --- TEXT ---

export type DxfTextEntity = DxfSupportedEntityBase & {
  type: "TEXT";
  geometry: {
    insertionPoint: DxfPoint3D;
    alignmentPoint?: DxfPoint3D;
    height: number;
    rotation?: number;
    contents: string;
    styleName?: string;
    widthFactor?: number;
    obliqueAngle?: number;
    hJustification?: number;
    vJustification?: number;
    generationFlags?: number;
  };
};

// --- MTEXT ---

export type DxfMTextEntity = DxfSupportedEntityBase & {
  type: "MTEXT";
  geometry: {
    insertionPoint: DxfPoint3D;
    nominalHeight: number;
    referenceWidth?: number;
    attachmentPoint?: number;
    drawingDirection?: number;
    contents: string;
    styleName?: string;
    rotation?: number;
    lineSpacingStyle?: number;
    lineSpacingFactor?: number;
    xAxisDirection?: DxfPoint3D;
    // group 210/220/230 — OCS handedness normal for a mirrored MTEXT (Codex #342
    // F3). The parser already populates `common.extrusion` and the writer's COMMON
    // emitter already emits it; this typed field lets the bridge project the model
    // `extrusion` through `common.extrusion` symmetrically with TEXT.
    extrusion?: DxfPoint3D;
    backgroundFillSetting?: number;
    fillBoxScale?: number;
    backgroundFillColorACI?: number;
    backgroundFillColorRGB?: number;
    backgroundTransparency?: number;
    columnFlowReversed?: boolean;
    columnAutoheight?: boolean;
    columnWidth?: number;
    columnGutter?: number;
    columnHeights?: number[];
    columnType?: number;
    columnCount?: number;
    horizontalWidth?: number;
    verticalHeight?: number;
  };
};

// --- DIMENSION ---

export type DxfDimensionEntity = DxfSupportedEntityBase & {
  type: "DIMENSION";
  geometry: {
    dimensionType: number;
    defPoint: DxfPoint3D;
    textMidpoint: DxfPoint3D;
    dimensionText?: string;
    styleName?: string;
    extLine1Point?: DxfPoint3D;
    extLine2Point?: DxfPoint3D;
    linearAngle?: number;
    measurement?: number;
    /* Phase 3: full-fidelity dimension fields */
    thirdPoint?: DxfPoint3D; // code 15/25/35
    arcPoint?: DxfPoint3D; // code 16/26/36
    obliqueAngle?: number; // code 51
    textRotation?: number; // code 52
    textAngleRelativeToDimLine?: number; // code 53
    leaderLength?: number; // code 40
    anonymousBlockName?: string; // code 2
    // Group 71 — MTEXT-style attachment point (1=Top-Left … 5=Middle-
    // Center … 9=Bottom-Right). AutoCAD default is 5. Common Dimension
    // Group Codes (Autodesk DXF Reference).
    attachmentPoint?: number; // code 71
    // S-DIMENSION-EXTRAS — AcDbDimension subclass fields previously dropped
    // at the typed-model boundary. All disambiguated against ezdxf
    // `ezdxf.entities.Dimension` + the existing DIMENSION_GROUP_READERS so
    // none collide with a code already consumed by DIMENSION.
    version?: number; // code 280 — AcDbDimension version flag (default 0)
    // Group 72/41 — MTEXT-style text line-spacing style + factor. ezdxf
    // `dxf.text_line_spacing_style` (72) / `dxf.text_line_spacing_factor`
    // (41). 41 is NOT consumed by any other DIMENSION reader (42=measurement,
    // 40=leaderLength), so it is safe within this per-type dispatch table.
    lineSpacingStyle?: number; // code 72 (1=AtLeast, 2=Exact)
    lineSpacingFactor?: number; // code 41
    // Group 12/22/32 — insertion point for clones of the dimension (Baseline
    // & Continue). ezdxf `Dimension.dxf.insert` (Vec3). Distinct from
    // defPoint (10/20/30) and textMidpoint (11/21/31).
    insertionPoint?: DxfPoint3D; // code 12/22/32
  };
};

// --- MLEADER (MULTILEADER) ---

export type DxfMLeaderBreakPoint = {
  start: DxfPoint3D;
  end: DxfPoint3D;
  breakIndex?: number; // 90 — LeaderLine breaks set it; LeaderData breaks omit it
};

export type DxfMLeaderLeaderLine = {
  vertices: DxfPoint3D[];
  // LeaderLine breaks — 90 break_index + 11/21/31 start + 12/22/32 end.
  breakPoints?: DxfMLeaderBreakPoint[];
  arrowheadBlock?: string;
  // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed LEADER_LINE fields
  // (CONTEXT_DATA depth 3, ezdxf LeaderLine). Mirrors mleaderLeaderLineSchema.
  leaderLineIndex?: number; // 91
  colorOverride?: string; // 92
  lineTypeHandle?: string; // 340 (R2010)
  lineType?: number; // 170
  lineWeightOverride?: string; // 171
  // Codex #268 — in-frame verbatim remainder. Any group inside THIS LEADER_LINE
  // (304…305) the builder could not promote to a typed field, kept in original
  // order so the writer re-emits it INSIDE the frame (not flat at the end of
  // CONTEXT_DATA), restoring AutoCAD/ezdxf structural-frame association.
  rawGroups?: DxfGroup[];
};

export type DxfMLeaderLeaderNode = {
  doglegVector?: DxfPoint3D; // 11/21/31 (iff hasDoglegVector)
  lines: DxfMLeaderLeaderLine[];
  doglegLength?: number; // 40
  // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed LEADER fields
  // (CONTEXT_DATA depth 2, ezdxf LeaderData). Mirrors mleaderLeaderNodeSchema.
  lastLeaderPoint?: DxfPoint3D; // 10/20/30 (iff hasLastLeaderLine)
  hasLastLeaderLine?: boolean; // 290
  hasDoglegVector?: boolean; // 291
  leaderIndex?: number; // 90
  attachmentDirection?: number; // 271 (R2010)
  // LeaderData breaks — alternating 12↔13 (start↔end) pairs, no index.
  breakPoints?: DxfMLeaderBreakPoint[];
  // Codex #268 — in-frame verbatim remainder. Any group inside THIS LEADER
  // (302…303) but OUTSIDE its LEADER_LINE sub-frames that the builder could not
  // promote to a typed field, kept in original order so the writer re-emits it
  // inside the LEADER frame (not flat at the end of CONTEXT_DATA).
  rawGroups?: DxfGroup[];
};

export type DxfMLeaderLeaderRoot = {
  nodes: DxfMLeaderLeaderNode[];
};

// Block-attribute reference captured from the DXF MLEADER entity body.
// AutoCAD encodes ATTDEF linkage as a repeating sequence of
// (330 / 177 / 44 / 302) groups — one tuple per ATTDEF inside the
// content block.
export type DxfMLeaderBlockAttributeReference = {
  attDefHandle: string; // group 330 — soft pointer to ATTDEF
  index: number; // group 177
  width: number; // group 44
  value: string; // group 302
};

// Per-annotative-scale snapshot parsed from MLEADER CONTEXT_DATA.
// Minimal field set for now — expanded as renderer needs grow.
export type DxfMLeaderAnnotativeContext = {
  scale: number;
  textHeight?: number;
  arrowheadSize?: number;
  doglegLength?: number;
  landingGap?: number;
  textRotation?: number;
  textColor?: string;
  textWidth?: number;
};

// S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed CONTEXT_DATA transport.
// Mirrors the drawing-model schemas (mleaderMTextDataSchema /
// mleaderBlockDataSchema / mleaderContextSchema) so the transport type carries
// exactly what the model carries. Distinct from DxfMLeaderAnnotativeContext
// above (the per-annotative-scale snapshot, B2); this is the singular
// structural CONTEXT_DATA record. Schema/types ONLY — parser (B) populates it.

// SCOPE 2 — MTextData (inline iff context has_mtext=290).
export type DxfMLeaderMTextData = {
  defaultContent?: string; // 304
  extrusion?: DxfPoint3D; // 11/21/31
  styleHandle?: string; // 340
  insert?: DxfPoint3D; // 12/22/32
  textDirection?: DxfPoint3D; // 13/23/33
  rotation?: number; // 42
  width?: number; // 43
  definedHeight?: number; // 44
  lineSpacingFactor?: number; // 45
  lineSpacingStyle?: number; // 170
  color?: string; // 90
  alignment?: number; // 171
  flowDirection?: number; // 172
  backgroundColor?: string; // 91
  backgroundScaleFactor?: number; // 141
  backgroundTransparency?: number; // 92
  useWindowBackground?: boolean; // 291
  hasBackgroundFill?: boolean; // 292
  columnType?: number; // 173
  useAutoHeight?: boolean; // 293
  columnWidth?: number; // 142
  columnGutter?: number; // 143
  columnFlowReversed?: boolean; // 294
  columnSizes?: number[]; // 144 (repeat per column)
  useWordBreak?: boolean; // 295
};

// SCOPE 3 — BlockData (inline iff context has_block=296).
export type DxfMLeaderBlockData = {
  blockRecordHandle?: string; // 341
  extrusion?: DxfPoint3D; // 14/24/34
  insert?: DxfPoint3D; // 15/25/35
  scale?: DxfPoint3D; // 16/26/36
  rotation?: number; // 46
  color?: string; // 93
  matrix?: number[]; // 47 ×16 (flat row-major)
};

// SCOPE 1 — MLeaderContext header (300{…301}).
export type DxfMLeaderContext = {
  scale?: number; // 40
  basePoint?: DxfPoint3D; // 10/20/30 (content base point; NOT plane origin)
  charHeight?: number; // 41
  arrowHeadSize?: number; // 140
  landingGapSize?: number; // 145
  textLeftAttachment?: number; // 174
  textRightAttachment?: number; // 175
  textAlignType?: number; // 176
  attachmentType?: number; // 177 (context attachment_type, NOT body connection-type)
  hasMText?: boolean; // 290
  hasBlock?: boolean; // 296
  mTextData?: DxfMLeaderMTextData; // present iff hasMText
  blockData?: DxfMLeaderBlockData; // present iff hasBlock
  planeOrigin?: DxfPoint3D; // 110/120/130
  planeXAxis?: DxfPoint3D; // 111/121/131
  planeYAxis?: DxfPoint3D; // 112/122/132
  planeNormalReversed?: boolean; // 297
  topAttachment?: number; // 272 (R2010)
  bottomAttachment?: number; // 273 (R2010)
};

export type DxfMLeaderEntity = DxfSupportedEntityBase & {
  type: "MLEADER";
  geometry: {
    contentType: number;
    textContents?: string;
    textHeight?: number;
    textRotation?: number;
    arrowheadSize?: number;
    arrowheadBlock?: string;
    // MTEXT text style for the MLEADER content. AutoCAD stores it as a
    // HANDLE (group 340 inside the CONTEXT_DATA / MTextData block —
    // ezdxf `MTextData.style_handle`, "handle of TextStyle table
    // entry"). The raw handle round-trips via `textStyleHandle`; the
    // parser's pass-2 resolver rehydrates the human-readable
    // `textStyleName` from the STYLE table (same mechanism as
    // MLEADERSTYLE group 340).
    textStyleHandle?: string; // code 340 (raw handle, for re-emit)
    textStyleName?: string; // resolved STYLE table name
    leaderRoots: DxfMLeaderLeaderRoot[];
    textLocation?: DxfPoint3D;
    styleName?: string;
    leaderStyleHandle?: string; // 340 body — MLEADERSTYLE handle (after CONTEXT_DATA)
    version?: number; // 270 body — AcDbMLeader version (default 2), first after marker
    landingEnabled?: boolean;
    doglegEnabled?: boolean;
    doglegLength?: number;
    landingGap?: number;
    textAttachmentType?: number;
    textAttachmentDirection?: number;
    textFrameDisplay?: boolean;
    textLineSpacingStyle?: number;
    textLineSpacingFactor?: number;
    textLineSpacingDistance?: number;
    textWidth?: number;
    leaderLineType?: number;
    leaderLineColor?: string;
    leaderLineWeight?: string;
    // AcDbMLeader BODY group 344 = block_record_handle (ezdxf Scope-6). RAW handle
    // round-trips here (writer re-emits 344 verbatim); pass-2 resolver rehydrates
    // the block NAME onto `blockName` (split like textStyleHandle→textStyleName; #271).
    blockRecordHandle?: string;
    blockName?: string;
    blockScale?: number;
    blockRotation?: number;
    blockScaleVector?: DxfPoint3D; // body 10/20/30 block_scale_vector (ezdxf write_vertex, dflt (1,1,1)); #432 r9
    // AcDbMLeader BODY group 176 block_connection_type (ezdxf Scope-6;
    // 0=center-extents, 1=insertion). Distinct from contextData.attachmentType
    // (context 177). Bridge-carried.
    blockConnectionType?: number;
    breakSize?: number;
    basePoint?: DxfPoint3D;
    // AcDbMLeader BODY group 90 (ezdxf Scope-6 `property_override_flags`).
    // gap-C.2: corrected from the prior body-175 mapping (175 is
    // text_alignment_type — see textAlignmentType below).
    propertyOverrideFlags?: number;
    // AcDbMLeader BODY group 175 (ezdxf Scope-6 `text_alignment_type`).
    // gap-C.2: 175 was previously mis-read as propertyOverrideFlags; per ezdxf
    // the override flags are body 90 and 175 is the text alignment enum.
    textAlignmentType?: number;
    // B1 — ATTDEF/ATTRIB linkage, repeating (330,177,44,302) groups
    blockAttributeReferences?: DxfMLeaderBlockAttributeReference[];
    // B2 — per-scale context data for annotative multileaders
    annotativeContexts?: DxfMLeaderAnnotativeContext[];
    // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — the single typed CONTEXT_DATA
    // record (ezdxf MLeaderContext). Populated by sub-slice B (parser).
    contextData?: DxfMLeaderContext;
    // True when the CONTEXT_DATA was wrapped in a `101 "Embedded Object"` /
    // `100 "AcDbMLeaderObjectContextData"` record. The writer re-wraps the
    // re-emitted CONTEXT_DATA in those markers instead of emitting it top-level
    // (which left a dangling 101/100, Codex #268).
    contextDataEmbedded?: boolean;
    // FULL typed tree of every CONTEXT_DATA block AFTER the first (annotative
    // per-scale blocks). The writer re-emits one CONTEXT_DATA block per entry,
    // after the primary; before this they were dropped (Codex #268). Empty/absent
    // for the common single-context MLEADER.
    secondaryContexts?: Array<{
      context: DxfMLeaderContext;
      leaderRoots: DxfMLeaderLeaderRoot[];
      // Unrecognised groups inside THIS secondary CONTEXT_DATA's header (or a
      // dropped leader/leader-line frame owned by it), preserved verbatim so the
      // writer re-emits them in this secondary block rather than leaking them
      // into the primary (Codex #285-9). Same shape as rawContextGroups.
      rawContextGroups?: DxfGroup[];
    }>;
    // Verbatim CONTEXT_DATA fallback channel — every context group code not yet
    // promoted to a typed field, preserved in original emit order
    // (byte-faithfulness, ПРИОРИТЕТ #1). Same {code,value} shape as extraGroups.
    rawContextGroups?: DxfGroup[];
    // Round-trip escape hatch for unknown MLEADER group codes
    extraMlVars?: Record<string, string>;
  };
};

// --- REVCLOUD (stored as LWPOLYLINE with appdata) ---

export type DxfRevcloudEntity = DxfSupportedEntityBase & {
  type: "REVCLOUD";
  geometry: {
    vertices: DxfLwPolylineVertex[];
    closed: boolean;
    // DXF group 70 bit 128 — plinegen; round-trips beside closed (1) @codex #422 P2
    plinegen?: boolean;
    constantWidth?: number;
    elevation?: number;
    vertexCount: number;
    arcLength: number;
    isCalloutstyle?: boolean;
    // RevcloudProps XData (AutoCAD 2021+): 1070 style (0=Normal,1=Calligraphy),
    // 1070 mode (0=Polygonal,1=Rectangular,2=Object), 1040 approx arc length,
    // 1040 min arc chord (→ `arcLength`, @codex #487 P2). Decoded from verbatim
    // `xdata` on import, re-encoded on export; absent fields fall back to schema.
    revisionCloudStyle?: string; // "Normal" | "Calligraphy"
    creationMode?: string; // "Rectangular" | "Polygonal" | "Object"
    approximateArcLength?: number;
  };
};

// --- WIPEOUT ---
// AcDbWipeout is a single-subclass entity (one `100 AcDbWipeout` marker) whose
// body IS the AcDbRasterImage group set. The typed model stores `vertices` in
// WORLD coords; the writer synthesizes the placement (insert = bbox min,
// u_pixel = (w,0), v_pixel = (0,h), image_size = (1,1)) and emits 14/24 as
// NORMALIZED ±0.5 image-space (ezdxf `Wipeout.set_masking_area`); the parser
// inverts it back to world (reconstructWipeoutWorldVertices). 71 rect/poly + 91
// are derived on write; `clipMode` (290, 0 outside/1 inside) round-trips. `frame`
// (AutoCAD display-frame toggle) is a DOCUMENT WIPEOUTVARIABLES setting, NOT a
// per-entity attribute — model/renderer/UI only, does NOT round-trip (see docs).
export type DxfWipeoutEntity = DxfSupportedEntityBase & {
  type: "WIPEOUT";
  geometry: {
    vertices: { x: number; y: number }[];
    frame: boolean;
    clipMode: 0 | 1; // DXF 290 — 0 = mask outside boundary, 1 = inside
  };
};

// --- HATCH ---

export type DxfHatchBoundaryEdgeLine = {
  edgeType: "line";
  start: DxfPoint3D;
  end: DxfPoint3D;
};

export type DxfHatchBoundaryEdgeArc = {
  edgeType: "arc";
  center: DxfPoint3D;
  radius: number;
  startAngle: number;
  endAngle: number;
  counterclockwise: boolean;
};

export type DxfHatchBoundaryEdgeEllipticArc = {
  edgeType: "ellipticArc";
  center: DxfPoint3D;
  majorAxisEndpoint: DxfPoint3D;
  minorAxisRatio: number;
  startAngle: number;
  endAngle: number;
  counterclockwise: boolean;
};

export type DxfHatchBoundaryEdge =
  | DxfHatchBoundaryEdgeLine
  | DxfHatchBoundaryEdgeArc
  | DxfHatchBoundaryEdgeEllipticArc;

export type DxfHatchBoundaryPath = {
  pathType: number;
  edges: DxfHatchBoundaryEdge[];
  // Associative source-boundary object handles (group 330) for THIS path,
  // emitted after the path's source-object count (group 97 > 0) when the
  // hatch is associative. Preserved verbatim for round-trip; absent for a
  // non-associative path. See model.ts hatchBoundaryPathSchema.
  sourceBoundaryHandles?: string[];
};

export type DxfHatchSeedPoint = {
  x: number;
  y: number;
};

// Custom pattern-definition line (S-HATCH-CUSTOM). One per hatch line family
// (DXF group 78 = count). `offset` is STRUCTURED {x,y} (groups 45/46), never
// flattened. `dashLengths` (group 49, count = group 79): + dash, - gap, 0 dot.
// See model.ts hatchPatternLineSchema. Source: DXF reference (AcDbHatch
// "Pattern data") + ezdxf `PatternLine`.
export type DxfHatchPatternLine = {
  angle: number;                  // 53
  base: { x: number; y: number }; // 43 / 44
  offset: { x: number; y: number }; // 45 / 46
  dashLengths: number[];          // 49 (count = 79)
};

// Gradient-fill extension (AcDbHatch). One color stop = (463 position,
// 63 ACI, 421 true-color). `trueColor` (group 421, 0xRRGGBB) is omitted
// when the source DXF had no 421 for that stop. See model.ts
// hatchGradientSchema for the full group-code mapping.
export type DxfHatchGradientColor = {
  position: number;     // 463
  aci?: number;         // 63 (omitted when the source stop carried no 63)
  trueColor?: number;   // 421 (0xRRGGBB)
};

export type DxfHatchGradient = {
  kind: number;            // 450
  reserved: number;        // 451 (reserved — preserve)
  oneColor: number;        // 452 (0=two-color, 1=single tint)
  numberOfColors: number;  // 453 (preserve as written)
  rotation: number;        // 460 radians
  shift: number;           // 461
  tint: number;            // 462
  name: string;            // 470
  colors: DxfHatchGradientColor[];
};

export type DxfHatchEntity = DxfSupportedEntityBase & {
  type: "HATCH";
  geometry: {
    patternName: string;
    solidFill: boolean;
    associative: boolean;
    hatchStyle?: number;
    patternType?: number;
    patternAngle?: number;
    patternScale?: number;
    // DXF group 77 — hatch pattern double-line flag (0=not double, 1=double;
    // ezdxf `Hatch.dxf.pattern_double`). Part of the entity-level pattern data,
    // after 41 scale, alongside 75/76, before 78. Absent ⇒ single-line (false).
    // (Group 73 is a BOUNDARY-path code, never the double flag.) Round-trips via
    // the dxf-io reader/writer (S-HATCH-CUSTOM).
    doubleLine?: boolean;
    // DXF groups 78 (count) + per-line 53/43/44/45/46/79/49 — custom
    // pattern-definition lines. Present only for custom (non-predefined,
    // non-solid) patterns; absent for solid fills / predefined patterns.
    // See model.ts hatchPatternLineSchema (full PAT-family, structured offset).
    customPatternLines?: DxfHatchPatternLine[];
    boundaryPaths: DxfHatchBoundaryPath[];
    // Elevation point (groups 10/20/30). `elevation` is the Z (group 30);
    // `elevationX`/`elevationY` (groups 10/20) are usually 0 but preserved
    // for full WCS-point round-trip. See model.ts hatchEntitySchema.
    elevation?: number;
    elevationX?: number;
    elevationY?: number;
    // DXF group 47 — SOLID/gradient pixel-size sampling density.
    pixelSize?: number;
    // DXF groups 210/220/230 — OCS extrusion normal. Absent ⇒ (0,0,1).
    extrusion?: DxfPoint3D;
    seedPoints?: DxfHatchSeedPoint[];
    // Present only for gradient hatches (group 450=1). Absent for
    // solid/pattern hatches so their DXF stays byte-identical.
    gradient?: DxfHatchGradient;
  };
};

// --- INSERT (block reference) ---

export type DxfInsertEntity = DxfSupportedEntityBase & {
  type: "INSERT";
  geometry: {
    blockName: string;
    insertionPoint: DxfPoint3D;
    scaleX?: number;
    scaleY?: number;
    scaleZ?: number;
    rotation?: number;
    columnCount?: number;
    rowCount?: number;
    columnSpacing?: number;
    rowSpacing?: number;
    // DXF group 66 — when 1, the entity is followed by one or more
    // ATTRIB entities terminated by a SEQEND. AutoCAD omits the code
    // (implying 0) when the block has no attribute definitions. We
    // preserve the flag explicitly so round-trip does not drop the 66.
    attributesFollow?: boolean;
    // Parsed ATTRIB children captured during the INSERT-ATTRIB*-SEQEND
    // grouping step in parseEntitiesSection. Empty or undefined when no
    // attributes follow the INSERT.
    attribs?: DxfAttribEntity[];
    // Handle of the terminating SEQEND, preserved so round-trip
    // restores the DXF unchanged.
    seqendHandle?: string;
    // DXF group 210/220/230 — extrusion direction (OCS). Optional; only
    // emitted when different from (0,0,1). Round-tripped separately from
    // the common extrusion because INSERT ships it inside its own
    // geometry block per the AutoCAD DXF reference.
    extrusion?: DxfPoint3D;
    // Per-INSERT representation data (Phase N.4 —
    // AcDbBlockRepresentationData). Captured from the INSERT's XData
    // and 102 extension-dict block; the bridge propagates the parsed
    // values into BlockReference.representationOverrides on import and
    // back out to XData / 102 group on export.
    representationData?: {
      // ETag value from AcDbBlockRepETag XData (group 1071). AutoCAD
      // increments this whenever the INSERT diverges further from the
      // block-level defaults; we round-trip the literal value.
      etag?: string;
      // Handle of the ACDB_BLOCKREPRESENTATION_DATA OBJECTS-section
      // record. Distinct from common.extensionDictionaryHandle (which
      // points at the AcDbBlockRepresentation DICTIONARY); the data
      // record is the dictionary's payload entry and carries the
      // parameter-override map that the bridge inflates into typed
      // overrides.
      dataHandle?: string;
      // Parameter override map keyed by parameter name. Populated by
      // the OBJECTS-section walker when the bridge resolves the
      // DICTIONARY chain (XDICTIONARY → AcDbBlockRepresentation →
      // ACDB_BLOCKREPRESENTATION_DATA). Empty / absent on simple
      // INSERTs that match the block-level defaults exactly.
      parameterOverrides?: Record<string, string | number | boolean>;
      // Per-INSERT current visibility state (state record name or id),
      // captured from the AcDbEnhancedBlockData payload. Distinct from
      // the active visibility state stored on the block-level
      // metadata; this field reflects the per-instance choice.
      currentVisibilityStateId?: string;
    };
  };
};

// --- ATTDEF (attribute definition, block-content placeholder) ---

// Decoded bit flags from DXF group 70 on ATTDEF / ATTRIB entities. All
// flags round-trip through the raw integer but we also decode each bit
// into a typed boolean so downstream code can reason about individual
// behaviours without bit-twiddling.
export type DxfAttribFlagBits = {
  invisible: boolean; // bit 1 (value 1)
  constant: boolean; // bit 2 (value 2)
  verify: boolean; // bit 3 (value 4)
  preset: boolean; // bit 4 (value 8)
  lockPosition: boolean; // bit 5 (value 16)
  multiline: boolean; // bit 6 (value 32)
};

export type DxfAttdefEntity = DxfSupportedEntityBase & {
  type: "ATTDEF";
  geometry: {
    insertionPoint: DxfPoint3D;
    alignmentPoint?: DxfPoint3D;
    height: number;
    defaultValue: string; // group 1
    tag: string; // group 2
    prompt: string; // group 3
    styleName?: string; // group 7
    rotation?: number; // group 50
    widthFactor?: number; // group 41
    obliqueAngle?: number; // group 51
    flags: number; // group 70 (raw bitfield)
    flagBits: DxfAttribFlagBits; // decoded flag bits
    generationFlags?: number; // group 71
    hJustification?: number; // group 72
    fieldLength?: number; // group 73
    vJustification?: number; // group 74
    lockPosition?: boolean; // group 280
    // MTEXT-ATTDEF background fill (group 90 / 63 / 420 / 441 / 45)
    backgroundFill?: number;
    backgroundFillColorACI?: number;
    backgroundFillColorRGB?: number;
    backgroundTransparency?: number;
    fillBoxScale?: number;
  };
};

// --- ATTRIB (attribute value — follows an INSERT) ---

export type DxfAttribEntity = DxfSupportedEntityBase & {
  type: "ATTRIB";
  geometry: {
    insertionPoint: DxfPoint3D;
    alignmentPoint?: DxfPoint3D;
    height: number;
    value: string; // group 1 (the actual attribute value)
    tag: string; // group 2 (tag that matches the ATTDEF)
    styleName?: string; // group 7
    rotation?: number; // group 50
    widthFactor?: number; // group 41
    obliqueAngle?: number; // group 51
    flags: number; // group 70 (raw bitfield)
    flagBits: DxfAttribFlagBits; // decoded flag bits
    generationFlags?: number; // group 71
    hJustification?: number; // group 72
    fieldLength?: number; // group 73
    vJustification?: number; // group 74
    lockPosition?: boolean; // group 280
    duplicateRecordCloningFlags?: number; // group 280 alt
    mtextAttribute?: boolean; // group 280 for MTEXT ATTRIB
    // Background fill group codes mirror ATTDEF
    backgroundFill?: number;
    backgroundFillColorACI?: number;
    backgroundFillColorRGB?: number;
    backgroundTransparency?: number;
    fillBoxScale?: number;
  };
};


// --- ARRAYRECT (stored as INSERT with ACDB_ASSOCARRAY xdata) ---

export type DxfArrayRectEntity = DxfSupportedEntityBase & {
  type: "ARRAYRECT";
  geometry: {
    blockName: string;
    insertionPoint: DxfPoint3D;
    scaleX?: number;
    scaleY?: number;
    scaleZ?: number;
    rotation?: number;
    columnCount: number;
    rowCount: number;
    levelCount?: number;
    // Grid spacing 44/45 are PRESENCE-AWARE (#251-B): `undefined` ⇔ group
    // ABSENT in source (never 0); an explicit `44 0`/`45 0` ⇒ present 0. The
    // AcDbAssocArray resolver applies the ObjectARX default (1) only when the
    // params omit spacing AND source 44/45 absent; explicit source 0 preserved
    // (preserve-source-never-infer). Mirrors DxfInsertEntity's optional spacing.
    // NOTE (SL-9): intentionally NO `associative` marker — the ARRAYRECT writer
    // arm ALWAYS emits `100 AcDbBlockReference` (associativity is not derivable
    // on the raw dxf-io path; MInsert deferred to SL-18). See writer.ts.
    columnSpacing?: number;
    rowSpacing?: number;
    levelSpacing?: number;
  };
};

// --- VIEWPORT ---

export type DxfViewportEntity = DxfSupportedEntityBase & {
  type: "VIEWPORT";
  geometry: {
    center: DxfPoint3D;
    width: number;
    height: number;
    viewCenter?: { x: number; y: number };
    viewHeight?: number;
    viewDirection?: DxfPoint3D;
    viewTarget?: DxfPoint3D;
    viewTwistAngle?: number;
    lensLength?: number;
    frontClipZ?: number;
    backClipZ?: number;
    viewportStatus?: number;
    viewportId?: number;
    statusBitFlags?: number;
    displayLocked?: boolean;
    shadePlotMode?: number;
    renderMode?: number;
    snapBase?: { x: number; y: number };
    snapSpacing?: { x: number; y: number };
    gridSpacing?: { x: number; y: number };
    // Batch-5 S-VIEWPORT-SNAPGRID — snap/grid scalars.
    // DXF group 50 (snap rotation, radians), 61 (major grid frequency, int),
    // 72 (circle zoom / smoothness percent).
    snapRotation?: number;
    gridMajor?: number;
    circleZoomPercent?: number;
    // ── Lighting block — view brightness / contrast (S-VIEWPORT-A,
    //    audit §12.1 «Contrast», truth-corrected) ─────────────────────
    // DXF group 141 = view BRIGHTNESS, group 142 = view CONTRAST.
    // Source of truth (all three agree):
    //   - Autodesk DXF reference, VIEWPORT entity (AcDbViewport):
    //     141 "View brightness", 142 "View contrast" (2007+/AC1021
    //     lighting block, alongside 282 default-lighting-type, 63/421/431
    //     ambient light color, 361 sun handle).
    //   - ezdxf 1.4.3 entities/viewport.py acdb_viewport:
    //     `"view_brightness": DXFAttr(141, dxfversion="AC1021")`,
    //     `"view_contrast":   DXFAttr(142, dxfversion="AC1021")`.
    //   - Real AutoCAD output (АБИ1.dxf): every VIEWPORT carries
    //     `141 0.0` / `142 0.0` between 282 and 63/421 — the lighting block.
    //
    // HISTORY (PR-13 mis-map, fixed by S-VIEWPORT-A): group 142 was
    // previously read as `viewportCannoscaleFactor` and 144 as an
    // "AcDbScale handle". Neither mapping exists in the Autodesk DXF
    // reference, in ezdxf, or in real AutoCAD files — 142 is contrast and
    // 144 is NOT a VIEWPORT group code at all. The real per-viewport
    // annotation scale lives in the viewport's extension dictionary:
    // XRECORD "ASDK_XREC_ANNOTATION_SCALE_INFO" → hard pointer 340 →
    // AcDbScale record in ACAD_SCALELIST (verified against АБИ1.dxf).
    // That OBJECTS graph round-trips verbatim via the dictionaries /
    // rawObjectGroups passthrough; an unread 144 now falls through to
    // `extraGroups` and round-trips verbatim through the model instead of
    // being typed at this layer and dropped at the bridge.
    brightness?: number;
    contrast?: number;
    // ── Per-viewport layer freeze + override soft-pointers (S-VIEWPORT-
    //    OVERRIDE-331, audit §12) ─────────────────────────────────────
    // DXF group 331 — soft-pointer ID/handle to the LAYER table records that
    // are FROZEN in this viewport (multiple entries may exist). Source of
    // truth: Autodesk DXF reference (AcDbViewport "frozen layer object
    // ID/handle, multiple entries may exist") + ezdxf `Viewport`
    // (`FROZEN_LAYER_GROUP_CODE = 331`, loaded into `_frozen_layers`, exported
    // one 331 per frozen layer). Without a typed reader these fell through to
    // `extraGroups` and were dropped at the model bridge of the time → per-VP
    // layer freeze silently lost on a model round-trip. (Today the S-COMMON-
    // XDATA base spread carries viewport extraGroups — pinned by the
    // S-VIEWPORT-A gc1/gc144 passthrough tests — but the handles stay TYPED so
    // the writer's single-emit drop-set discipline applies to them.)
    // Handle-preservation only; resolving handles → layer
    // names (and the per-VP render arm) is the separate, deferred RESOLVE
    // slice.
    frozenLayerHandles?: string[];
    // DXF groups 335 / 343 / 344 / 91 — soft-/hard-pointer references to the
    // viewport's per-VP layer PROPERTY-override objects (color / linetype /
    // lineweight overrides). The Autodesk DXF reference labels all four
    // "Soft pointer reference to viewport object (for layer VP property
    // override)"; ezdxf models them as `ref_vp_object_1..4`. They are
    // preserved verbatim as typed {code, handle} pairs (NOT collapsed to a
    // flat bag — the exact group code per entry is kept so the writer re-emits
    // them byte-faithfully) so a model round-trip does not silently drop them.
    // The semantic resolution (handle → override VALUE) is the deferred
    // S-VIEWPORT-OVERRIDE-RESOLVE slice.
    layerOverrideHandles?: { code: number; handle: string }[];
    // ── Clipping-boundary handle (S-VIEWPORT-CLIP-HANDLE, EPIC-VIEWPORT-CLIP
    //    slice 1) ───────────────────────────────────────────────────────────
    // DXF group 340 — hard-pointer ID/handle to the graphical entity in
    // paperspace (LWPOLYLINE / CIRCLE / ELLIPSE / closed SPLINE) that defines
    // this viewport's non-rectangular clip boundary. Source of truth: ezdxf
    // 1.4.3 entities/viewport.py acdb_viewport `"clipping_boundary_handle":
    // DXFAttr(340, default="0", optional=True)` (`has_extended_clipping_path`
    // checks this handle is non-zero) + Autodesk DXF reference AcDbViewport.
    // Round-trips verbatim (preserve-source — a literal "0" means "no clip" and
    // is kept). This is the ENTITY-BODY 340 — distinct from the ext-dict XRECORD
    // 340 → AcDbScale annotation-scale pointer (OBJECTS section, untouched here).
    // Handle-preservation only; the SPATIAL_FILTER producer + resolveViewportClip
    // resolver + clip render are the deferred slices 2/3 (S-OBJECTS-SPATIAL-
    // FILTER / S-VIEWPORT-CLIP-RESOLVE). Absent ⇒ no clip handle in source.
    clippingBoundaryHandle?: string;
  };
};

// Legacy heavy-POLYLINE family + ACAD_TABLE / OLE2FRAME live in type-only
// leaf modules (types.ts is at the 2000-line ceiling). Re-exported here so
// every existing `from "./types"` / `from "@dxf-ai-editor/dxf-io"` import
// path keeps working unchanged.
export type {
  DxfPolylineEntity,
  DxfVertexEntity,
  DxfPolylineSubtype,
} from "./types.legacyPolyline";
export {
  DXF_POLYLINE_FLAG,
  DXF_VERTEX_FLAG,
  DXF_VERTEX_FACE_INDEX_CODES,
  dxfPolylineSubtype,
  dxfVertexSubtype,
} from "./types.legacyPolyline";
export type {
  DxfAcadTableEntity,
  DxfOle2FrameEntity,
} from "./types.tableOle";

import type {
  DxfPolylineEntity,
  DxfVertexEntity,
} from "./types.legacyPolyline";
import type {
  DxfAcadTableEntity,
  DxfOle2FrameEntity,
} from "./types.tableOle";

// --- Union of all supported entities ---

export type DxfSupportedEntity =
  | DxfLineEntity
  | DxfPointEntity
  | DxfCircleEntity
  | DxfArcEntity
  | DxfEllipseEntity
  | DxfSplineEntity
  | DxfLwPolylineEntity
  | DxfTextEntity
  | DxfMTextEntity
  | DxfDimensionEntity
  | DxfMLeaderEntity
  | DxfRevcloudEntity
  | DxfWipeoutEntity
  | DxfHatchEntity
  | DxfInsertEntity
  | DxfArrayRectEntity
  | DxfViewportEntity
  | DxfAttdefEntity
  | DxfAttribEntity
  | DxfSeqendEntity
  | DxfPolylineEntity
  | DxfVertexEntity
  | DxfAcadTableEntity
  | DxfOle2FrameEntity;

export type DxfUnsupportedEntity = {
  kind: "unsupported-entity";
  type: string;
  groups: DxfGroup[];
};

export type DxfEntity = DxfSupportedEntity | DxfUnsupportedEntity;

export type DxfRawSection = {
  kind: "raw-section";
  name: string;
  groups: DxfGroup[];
};

export type DxfEntitiesSection = {
  kind: "entities-section";
  entities: DxfEntity[];
};

export type DxfTextStyleEntry = {
  name: string;
  handle?: string;
  // T9b — group 330, the owning STYLE table-head handle. Every R13+ symbol-
  // table record carries its owner (Autodesk DXF reference "Common Symbol
  // Table Group Codes"; ezdxf writes `330 <table handle>` on every record).
  ownerHandle?: string;
  fontName?: string;
  bigFontFile?: string;
  height?: number;
  widthFactor?: number;
  obliqueAngle?: number;
  flags?: number;
  lastHeight?: number;
  generationFlags?: number;
  fontCharset?: number;
  // Whether the SOURCE record carried the `100/AcDbSymbolTableRecord` +
  // `100/AcDbTextStyleTableRecord` subclass markers (R13+ DXFs). `false` ⇒ a
  // parsed R12 / marker-less record (the writer must NOT synthesise the two
  // 100/ groups — that breaks byte-faithful carry-forward). `undefined` ⇒ a
  // freshly-built / skeleton record (the writer emits the markers, the R13+
  // default). Mirrors `DxfLinetypeEntry.hadSubclassMarker`.
  hadSubclassMarker?: boolean;
};

export type DxfDimensionStyleEntry = {
  name: string;
  handle?: string;
  // T9b — group 330, the owning DIMSTYLE table-head handle (see
  // `DxfTextStyleEntry.ownerHandle`).
  ownerHandle?: string;
  dimVars: Record<string, string | number>;
  // See DxfTextStyleEntry.hadSubclassMarker — `false` for a parsed marker-less
  // R12 DIMSTYLE (suppress the `100/AcDbSymbolTableRecord` +
  // `100/AcDbDimStyleTableRecord` groups), `undefined` for a fresh record
  // (emit them).
  hadSubclassMarker?: boolean;
};

export type DxfLayerEntry = {
  name: string;
  handle?: string;
  // T9b — group 330, the owning LAYER table-head handle (see
  // `DxfTextStyleEntry.ownerHandle`).
  ownerHandle?: string;
  color: number; // group 62; negative = layer off
  flags: number; // group 70; bit 1=frozen, bit 4=locked
  linetype: string; // group 6
  lineweight: number; // group 370; -3 = Default
  plottable: boolean; // group 290
  // T9b — group 390 (R2000+), hard-pointer to the layer's PlotStyleName
  // object (an ACDBPLACEHOLDER in the ACAD_PLOTSTYLENAME dictionary; AutoCAD /
  // ezdxf default it to the `Normal` entry). Only meaningful when the target
  // object is emitted — never fabricated.
  plotStyleHandle?: string;
  // T9b — group 347 (R2007+), hard-pointer to the layer's MATERIAL object
  // (AutoCAD / ezdxf default it to the `ByLayer` material). Same emit-only-when-
  // resolvable contract as `plotStyleHandle`.
  materialHandle?: string;
  description?: string; // group 1001 (ACAD-specific extension)
};

// BLOCK_RECORD entries live inside the TABLES section. Each entry
// carries metadata about a block definition that complements the data
// captured in the BLOCKS section (insertion units, explodability,
// scalability, associated LAYOUT handle, annotative flag). AutoCAD
// requires at least `*Model_Space` and `*Paper_Space` entries to be
// present.
export type DxfBlockRecordEntry = {
  name: string; // group 2
  handle?: string; // group 5
  ownerHandle?: string; // group 330 — owner (BLOCK_RECORD table handle)
  insertionUnits?: number; // group 70 (0-21: 0=unitless, 1=in, 4=mm, ..., 21=US survey ft)
  explodability?: number; // group 280 (0=not allowed, 1=allowed)
  scalability?: number; // group 281 (0=non-uniform, 1=uniform)
  layoutHandle?: string; // group 340 — LAYOUT object reference
  // XData (ACAD) flags — annotative blocks store AcDbBlockRepresentation
  // XData group tuples. We capture the raw XData record so it round-trips
  // and decode the common `Annotative` flag for ergonomic consumption.
  isAnnotative?: boolean; // decoded from ACAD XData (1070 / 1071)
  xdata?: DxfXDataEntry[];
  // Handle of the OBJECTS-section DICTIONARY referenced by
  // `102 {ACAD_XDICTIONARY … 360 <handle> 102 }` on this record.
  // For dynamic blocks that dictionary owns an ACAD_ENHANCEDBLOCK child
  // (the eval-graph container) and is the canonical marker used by
  // AutoCAD 2007+ to distinguish dynamic vs static BLOCK_RECORDs.
  extensionDictionaryHandle?: string;
  // Names of persistent-reactor app callbacks attached via
  // `102 {ACAD_REACTORS … 330 <handle> 102 }`. Captured verbatim for
  // round-trip; static blocks never have any reactor handles here.
  reactorHandles?: string[];
  // ── Annotative decomposition map (PR-2) ──
  // For annotative blocks, AutoCAD synthesises an anonymous block
  // (`*A_NNN` naming convention) per materialised annotation scale and
  // records the mapping inside the BLOCK_RECORD's `AcadAnnotativeDecomposition`
  // XData entry. This typed field surfaces the decoded
  // `scaleName → anonymousBlockHandle` mapping so the bridge layer can
  // populate `BlockRecord.annotativeDecompositions` without re-walking
  // the XData groups. Empty / undefined for non-annotative blocks.
  annotativeDecompositions?: Record<string, string>;
  // Unknown / vendor-specific group codes are preserved verbatim so the
  // writer can re-emit them.
  extraGroups?: DxfGroup[];
};

// One element of an LTYPE pattern. Simple elements carry only `length`
// (signed group 49: >0 dash, <0 gap, =0 dot). Complex elements add an
// embedded text string or shape glyph that AutoCAD draws inside the
// pattern via codes 74/75/340/46/50/44/45/9 (DXF LTYPE reference). The
// raw signed `length` is preserved verbatim so the writer round-trips the
// exact group-49 byte without re-deriving the sign from a magnitude.
export type DxfLinetypeElement = {
  /** group 49 — signed dash (>0) / gap (<0) / dot (=0) length. */
  length: number;
  /** group 74 — element type bitfield (bit1 abs-rotation, bit2 text, bit4 shape). */
  elementType?: number;
  /** group 75 — shape number (complex shape elements). */
  shapeNumber?: number;
  /** group 340 — STYLE (text style / shape-file style) handle. */
  styleHandle?: string;
  /** group 46 — scale (S=). */
  scale?: number;
  /** group 50 — rotation, RADIANS in DXF. */
  rotation?: number;
  /** group 44 — x offset (X=). */
  offsetX?: number;
  /** group 45 — y offset (Y=). */
  offsetY?: number;
  /** group 9 — text string (complex text elements). */
  text?: string;
};

// A parsed LTYPE table record. Mirrors `DxfLayerEntry` / `DxfTextStyleEntry`
// — a flat typed group-code projection that the bridge converts into the
// drawing-model `Linetype` schema. `patternLength` (group 40) and
// `elementCount` (group 73) keep the file's RAW values so a plain round-trip
// re-emits them byte-exact even when they disagree with `Σ|element length|` /
// `elements.length` (a loadable-but-dirty file). `extraGroups` preserves any
// unrecognised codes (vendor extensions) for verbatim re-emission.
export type DxfLinetypeEntry = {
  name: string; // group 2
  handle?: string; // group 5
  ownerHandle?: string; // group 330
  description?: string; // group 3
  flags?: number; // group 70 (16 xrefDep, 32 xrefResolved, 64 referenced)
  alignment?: number; // group 72 (always 65 = 'A')
  /** group 73 — element count, RAW from the file. */
  elementCount?: number;
  /** group 40 — total pattern length, RAW from the file. */
  patternLength?: number;
  elements: DxfLinetypeElement[];
  /** Unknown / vendor-specific group codes, preserved verbatim. */
  extraGroups?: DxfGroup[];
  /**
   * FIX-B (Codex#2 PR-LT2 P2) — whether the source record carried the
   * `100/AcDbLinetypeTableRecord` subclass marker. R12 / minimal DXFs omit it;
   * the writer re-synthesises a `100` only when this is `true` (or when the
   * entry is freshly built and never set the flag — defaults to emit). Absent =
   * the source had no marker, so the writer must NOT add one (byte parity).
   */
  hadSubclassMarker?: boolean;
};

export type DxfTablesSection = {
  kind: "tables-section";
  textStyles: DxfTextStyleEntry[];
  dimensionStyles: DxfDimensionStyleEntry[];
  layers: DxfLayerEntry[];
  blockRecords: DxfBlockRecordEntry[];
  // LTYPE table records — promoted out of `rawTableGroups` into a typed array
  // (PR-LT2); when non-empty the writer serialises this typed table once (no
  // raw-passthrough duplication). Older callers default to `[]` (raw passthrough).
  linetypes?: DxfLinetypeEntry[];
  // Captured LTYPE table-LEVEL header (`5/<hex> 330/0 100/AcDbSymbolTable
  // 70/<count>` + vendor codes, between `2/LTYPE` and the first `0/LTYPE`) so the
  // writer re-emits the real head, not a synthesised one (Codex#2 P2). Excludes
  // the `0/TABLE` / `2/LTYPE` / per-record / `0/ENDTAB` markers. Absent ⇒ minimal.
  linetypeTableHeader?: DxfGroup[];
  // FIX-110 — captured `5/<hex> 330/0 100/AcDbSymbolTable 70/<count>` table heads
  // for LAYER / STYLE / DIMSTYLE / BLOCK_RECORD, keyed by table name (generalises
  // `linetypeTableHeader`; see sectionParsers/writer). Dropping it → ezdxf repair.
  symbolTableHeaders?: Record<string, DxfGroup[]>;
  rawTableGroups: DxfGroup[];
};

/* ── OBJECTS SECTION ─────────────────────────────── */
// One entry inside a DICTIONARY object. AutoCAD splits references into
// soft pointers (group 350) and hard owner pointers (group 360); we
// capture both so the two-pass handle resolver can rehydrate whichever
// kind it needs.
export type DxfDictionaryReference = {
  name: string; // group 3
  handle: string; // group 350 or 360
  isHard: boolean; // true when the pointer came from group 360
};

export type DxfDictionaryEntry = {
  kind: "dictionary";
  handle: string; // group 5
  ownerHandle?: string; // group 330 — soft pointer to owner
  hardOwned: boolean; // group 280 — 0/1 flag
  cloning: number; // group 281 — cloning flag (default 1 = keep)
  entries: DxfDictionaryReference[];
  // ── Batch-6 S-OBJECTS-DICT-ROUNDTRIP verbatim preservation ──
  // True when the record's 0-marker was `ACDBDICTIONARYWDFLT` (a DICTIONARY
  // subclass that carries a default-entry handle). Preserved so the writer
  // re-emits the correct 0-marker + `100/AcDbDictionaryWithDefault` subclass.
  isWithDefault?: boolean;
  // Group 340 — default-entry hard handle on ACDBDICTIONARYWDFLT records.
  defaultHandle?: string;
  // Handle of the OBJECTS-section DICTIONARY referenced by the nested
  // `102 {ACAD_XDICTIONARY … 360 <handle> 102 }` block. Captured so the
  // writer reconstructs the 102 pair verbatim (mirror BLOCK_RECORD).
  extensionDictionaryHandle?: string;
  // Persistent-reactor handles from `102 {ACAD_REACTORS … 330 <handle> 102 }`.
  reactorHandles?: string[];
  // XData records (1001-1071), captured verbatim for round-trip.
  xdata?: DxfXDataEntry[];
  // Unknown / vendor-specific group codes preserved verbatim so the writer
  // can re-emit them (mirror BLOCK_RECORD extraGroups).
  extraGroups?: DxfGroup[];
};

// Fully-parsed MLEADERSTYLE object. Every AutoCAD-documented group code
// has a home here; unknown codes round-trip through extraGroups. Handle
// fields (340/341/342/343) stay as strings until the two-pass resolver
// can rehydrate them.
export type DxfMultileaderStyleEntry = {
  kind: "mleader-style";
  handle: string; // group 5
  ownerHandle?: string; // group 330
  name: string; // group 2

  // ── Content ──
  contentType?: number; // 170 — 0=None, 1=Block, 2=MText
  textStyleHandle?: string; // 342 — STYLE handle (ezdxf acdb_mleader_style, VERIFIED 1.4.3)
  textStyleName?: string; // resolved name (two-pass)
  textLeftAttachmentType?: number; // 174
  textAngleType?: number; // 175
  textAlignmentType?: number; // 176
  blockContentConnectionType?: number; // 177
  maxLeaderSegmentPointsNumber?: number; // 90
  firstSegmentAngleConstraint?: number; // 40
  secondSegmentAngleConstraint?: number; // 41
  leaderType?: number; // 173 — 0=Invisible, 1=Straight, 2=Spline (ezdxf leader_type)
  leaderLineColor?: string; // 91 (ACI color index as string) or resolved
  leaderLineTypeHandle?: string; // 340 — handle to LTYPE entry
  leaderLineType?: string; // resolved linetype name (two-pass)
  leaderLineWeight?: string; // 92
  enableLanding?: boolean; // 290
  enableDogleg?: boolean; // 291
  landingDistance?: number; // 42 — landing_gap_size (ezdxf acdb_mleader_style)
  doglegLength?: number; // 43 — dogleg_length (ezdxf acdb_mleader_style)
  description?: string; // 3
  arrowheadBlockHandle?: string; // 341 — handle to BLOCK_RECORD (ezdxf arrow_head_handle)
  arrowheadBlock?: string; // resolved block name (two-pass)
  arrowheadSize?: number; // 44
  textDefault?: string; // 300
  textLeftAttachmentTypeDeprecated?: number; // 172 (ezdxf draw_leader_order_type)
  textRightAttachmentType?: number; // 178 — ezdxf text_right_attachment_type
  textBottomAttachmentType?: number; // 272
  textTopAttachmentType?: number; // 273
  textColor?: string; // 93
  textHeight?: number; // 45
  textFrameEnabled?: boolean; // 292
  textAlignAlwaysLeft?: boolean; // 297
  alignSpace?: number; // 46
  blockContentHandle?: string; // 343 — handle to BLOCK_RECORD (ezdxf block_record_handle)
  blockContentName?: string; // resolved block name (two-pass)
  blockContentColor?: string; // 94
  blockContentScaleX?: number; // 47
  blockContentScaleY?: number; // 49
  blockContentScaleZ?: number; // 140
  enableBlockContentScale?: boolean; // 293
  blockContentRotation?: number; // 141
  enableBlockContentRotation?: boolean; // 294
  scaleOverall?: number; // 142
  enableAnnotationScale?: boolean; // 296
  breakGapSize?: number; // 143
  textAttachmentDirection?: number; // 271
  bottomTextAttachmentDirection?: number; // 272
  topTextAttachmentDirection?: number; // 273
  textAttachmentTypeBottom?: number; // 274
  // Unknown / vendor-extension group codes
  extraGroups: Record<string, string>;
};

// LAYOUT object parsed from the OBJECTS section. Every paper-space tab
// in a DXF has one LAYOUT entry owned by a `*Paper_Space`/`*Paper_Space<N>`
// BLOCK_RECORD. AutoCAD's tab bar and the File > Page Setup dialog read
// their data from this record, not from the BLOCKS / BLOCK_RECORD tables.
// Handle codes 330 (owner BLOCK_RECORD) and 340 (reverse pointer on the
// BLOCK_RECORD) form the link pair.
export type DxfLayoutEntry = {
  kind: "layout";
  handle: string; // group 5
  ownerHandle?: string; // group 330 (AcDbLayout subclass) — owning BLOCK_RECORD
  // group 330 in the AcDbPlotSettings-leading context — the ACAD_LAYOUT
  // dictionary that owns this LAYOUT object. Distinct from `ownerHandle` (the
  // BLOCK_RECORD link). Preserved so the writer re-emits BOTH 330s in their
  // correct positions (ezdxf pairs LAYOUT↔BLOCK_RECORD by the AcDbLayout 330).
  dictionaryOwnerHandle?: string;
  name: string; // group 1 — tab name ("Model", "Layout1", …) — AcDbLayout block
  tabOrder?: number; // group 71
  // ── PLOTSETTINGS sub-object (AcDbPlotSettings 100-marker) ──
  // AutoCAD writes a full PLOTSETTINGS group block BEFORE the AcDbLayout
  // marker inside each LAYOUT object. The parser disambiguates the codes
  // that appear in BOTH blocks (1 / 70 / 76) by tracking the active
  // 100-subclass marker (see parseLayoutEntry).
  pageSetupName?: string; // group 1 (under AcDbPlotSettings) — page-setup name
  paperSize?: string; // group 4 — paper-size name
  plotViewName?: string; // group 6 — named plot view
  plotMargins?: {
    left: number; // group 40
    bottom: number; // group 41
    right: number; // group 42
    top: number; // group 43
  };
  paperWidthMm?: number; // group 44 — physical paper width (mm)
  paperHeightMm?: number; // group 45 — physical paper height (mm)
  plotPaperUnits?: number; // group 72 — 0=in, 1=mm, 2=pixels
  plotRotation?: number; // group 73 — 0..3 (0°, 90°, 180°, 270°)
  plotAreaType?: number; // group 74 — 0..5 (Display/Extents/Limits/View/Window/Layout)
  plotOrigin?: { x: number; y: number }; // group 46/47 — plot offset
  plotWindow?: {
    lowerLeftX: number; // group 48
    lowerLeftY: number; // group 49
    upperRightX: number; // group 140
    upperRightY: number; // group 141
  };
  plotScale?: {
    numerator: number; // group 142 — real-world units
    denominator: number; // group 143 — drawing units
  };
  plotLayoutFlags?: number; // group 70 (under AcDbPlotSettings) — plot layout flag bitfield
  plotStyleName?: string; // group 7 — current plot-style sheet (CTB/STB) name
  minimumLimits?: { x: number; y: number }; // group 10/20
  maximumLimits?: { x: number; y: number }; // group 11/21
  insertionBase?: DxfPoint3D; // group 12/22/32
  minimumExtents?: DxfPoint3D; // group 14/24/34
  maximumExtents?: DxfPoint3D; // group 15/25/35
  elevation?: number; // group 146
  orthographicViewType?: number; // group 76
  // FIX E (round-9 #4) — nested `102` reactor / xdictionary block(s) in the LAYOUT
  // HEADER slot (after the 5/330 owner, BEFORE the first `100` marker). A SEPARATE
  // positional bucket (distinct from the tail `extraGroups`) so the writer re-emits
  // them in their ORIGINAL slot — byte-faithful instead of relocating to the tail.
  // Post-subclass 102 blocks still flow through `extraGroups` (canonical tail slot).
  headerExtraGroups?: DxfGroup[];
  // Unknown group codes round-trip verbatim.
  extraGroups?: DxfGroup[];
};

// ── DYNAMIC BLOCK OBJECTS (Tier 1) ───────────────────
// AutoCAD stores dynamic block authoring data as a cluster of objects in
// the OBJECTS section, linked to a BLOCK_RECORD via its Extension
// Dictionary. A typical dynamic block emits (one each per parameter /
// action / visibility state):
//   * ACDBBLOCKREPRESENTATION       — anchor owned by the BLOCK_RECORD
//                                     XDictionary; references all
//                                     parameters + actions + eval graph.
//   * ACDBEVALGRAPH                 — dependency graph node (edges of
//                                     parameter → action).
//   * ACDBBLOCK<kind>PARAMETER      — one per parameter (LINEAR, FLIP,
//                                     VISIBILITY, BASEPOINT, ...).
//   * ACDBBLOCKVISIBILITYSTATE      — child of VISIBILITY parameter.
//   * ACDBBLOCK<kind>ACTION         — one per action (MOVE, STRETCH,
//                                     FLIP, ...).
//   * ACDBDYNAMICBLOCKPURGEPREVENTER — "don't purge" marker.
//
// Most of these classes are only partially documented in the public
// DXF reference. We retain every group code in `rawGroups` so the writer
// can re-emit the object byte-for-byte, while also decoding the fields
// we understand into typed members. Downstream (`dxfBridge.dynamic.ts`)
// extracts the typed fields into the DrawingModel's
// `DynamicBlockMetadata`.
export type DxfDynamicBlockObjectType =
  | "ACDBBLOCKREPRESENTATION"
  | "ACDBEVALGRAPH"
  // Tier 1 parameters
  | "ACDBBLOCKLINEARPARAMETER"
  | "ACDBBLOCKFLIPPARAMETER"
  | "ACDBBLOCKVISIBILITYPARAMETER"
  | "ACDBBLOCKBASEPOINTPARAMETER"
  | "ACDBBLOCKVISIBILITYSTATE"
  // Tier 1 actions
  | "ACDBBLOCKMOVEACTION"
  | "ACDBBLOCKSTRETCHACTION"
  | "ACDBBLOCKFLIPACTION"
  | "ACDBDYNAMICBLOCKPURGEPREVENTER"
  // Tier 2 parameters (DYN-2)
  | "ACDBBLOCKPOINTPARAMETER"
  | "ACDBBLOCKPOLARPARAMETER"
  | "ACDBBLOCKXYPARAMETER"
  | "ACDBBLOCKROTATIONPARAMETER"
  | "ACDBBLOCKLOOKUPPARAMETER"
  // Tier 2 actions (DYN-2)
  | "ACDBBLOCKROTATIONACTION"
  | "ACDBBLOCKSCALEACTION"
  | "ACDBBLOCKARRAYACTION"
  | "ACDBBLOCKLOOKUPACTION"
  // Tier 2 lookup / block properties table (DYN-2)
  | "ACDBBLOCKPROPERTIESTABLE"
  | "ACDBBLOCKPROPERTIESTABLEGRIP";

export type DxfDynamicBlockObject = {
  kind: "dynamic-block-object";
  type: DxfDynamicBlockObjectType | string; // tolerate unknown Tier-N classes
  handle: string; // group 5
  ownerHandle?: string; // group 330
  // Every DXF group preserved verbatim (including 0/100 markers). Round-
  // trip writer emits these back so unsupported Tier-N codes survive a
  // load/save cycle even if the bridge layer does not decode them.
  rawGroups: DxfGroup[];
};

export function isSupportedDynamicBlockType(value: string): boolean {
  return (
    value === "ACDBBLOCKREPRESENTATION" ||
    value === "ACDBEVALGRAPH" ||
    // Tier 1
    value === "ACDBBLOCKLINEARPARAMETER" ||
    value === "ACDBBLOCKFLIPPARAMETER" ||
    value === "ACDBBLOCKVISIBILITYPARAMETER" ||
    value === "ACDBBLOCKBASEPOINTPARAMETER" ||
    value === "ACDBBLOCKVISIBILITYSTATE" ||
    value === "ACDBBLOCKMOVEACTION" ||
    value === "ACDBBLOCKSTRETCHACTION" ||
    value === "ACDBBLOCKFLIPACTION" ||
    value === "ACDBDYNAMICBLOCKPURGEPREVENTER" ||
    // Tier 2 (DYN-2)
    value === "ACDBBLOCKPOINTPARAMETER" ||
    value === "ACDBBLOCKPOLARPARAMETER" ||
    value === "ACDBBLOCKXYPARAMETER" ||
    value === "ACDBBLOCKROTATIONPARAMETER" ||
    value === "ACDBBLOCKLOOKUPPARAMETER" ||
    value === "ACDBBLOCKROTATIONACTION" ||
    value === "ACDBBLOCKSCALEACTION" ||
    value === "ACDBBLOCKARRAYACTION" ||
    value === "ACDBBLOCKLOOKUPACTION" ||
    value === "ACDBBLOCKPROPERTIESTABLE" ||
    value === "ACDBBLOCKPROPERTIESTABLEGRIP" ||
    // Round-trip envelope for Tier-N classes that mention "BLOCK" in
    // their class name. Anything starting with ACDBBLOCK* or ACDBDYN*
    // is safe to treat as a dynamic-block object for preservation.
    /^ACDBBLOCK.+/.test(value) ||
    /^ACDBDYN.+/.test(value) ||
    // AutoCAD 2018+ emits dynamic-block classes WITHOUT the ACDB prefix.
    // Accept the symmetric un-prefixed spelling so constraint-based parametric
    // blocks (BLOCKHORIZONTALCONSTRAINTPARAMETER, BLOCKPROPERTIESTABLE,
    // BLOCKUSERPARAMETER, BLOCKLINEARGRIP, BLOCKGRIPLOCATIONCOMPONENT,
    // BLOCKPROPERTIESTABLEGRIP, BLOCKVISIBILITYGRIP, ...) reach the
    // downstream dynamic-block parser instead of falling into rawObjectGroups.
    //
    // The `.*` (zero or more) between `BLOCK` and the trailing token is
    // intentional — AutoCAD occasionally emits a bare `BLOCK<token>`
    // (e.g. `BLOCKGRIPLOCATIONCOMPONENT`). Longer alternatives are
    // listed first so the regex engine does not settle for a shorter
    // prefix match and miss the full-suffix form.
    /^BLOCK.*(GRIPLOCATIONCOMPONENT|PROPERTIESTABLEGRIP|PROXYNODE|PARAMETER|ACTION|STATE|TABLE|GRIP)$/.test(
      value,
    ) ||
    value === "BLOCKUSERPARAMETER" ||
    value === "BLOCKPROPERTIESTABLE" ||
    value === "BLOCKPROPERTIESTABLEGRIP" ||
    value === "ACDB_DYNAMICBLOCKPROXYNODE"
  );
}

/**
 * Which array-parameter family a parameters record belongs to. The rectangular
 * case is fully decoded; polar/path are forward-compat (preserved verbatim,
 * family tagged so a later phase can widen the decode). (Batch-8 S-ARRAYASSOC.)
 */
export type AssocArrayParamFamily =
  | "rectangular"
  | "polar"
  | "path"
  | "modify"
  | "none";

/**
 * Decoded rectangular AcDbAssocArray parameter snapshot. Every field is OPTIONAL
 * (AutoCAD omits a group at its default; polar/path contribute none). The
 * resolver layers these onto the model schema. The positional `intParams`/
 * `doubleParams`/`expressions` arrays preserve the raw ordered values so the
 * resolver can apply whichever convention a producer used without data loss.
 */
export type AssocArrayRectParams = {
  /** Column count (items along X). */
  columnCount?: number;
  /** Row count (items along Y). */
  rowCount?: number;
  /** Level count (Z-stack copies — 3D array). */
  levelCount?: number;
  /** Column spacing. */
  columnSpacing?: number;
  /** Row spacing. */
  rowSpacing?: number;
  /** Level spacing (Z distance between adjacent levels). */
  levelSpacing?: number;
  /** Axis angle, as emitted by SOURCE — unit VARIES by form (AW-1 units seam, see `axesAngleRadiansToModelDegrees` in handleResolution.ts). */
  axisAngle?: number;
  /** Per-row Z elevation increment (3D rectangular array). */
  rowElevation?: number;
  /** Ordered group-90 ints after the rect-params marker (positional fallback). */
  intParams: number[];
  /** Ordered group-40 doubles after the rect-params marker (positional fallback). */
  doubleParams: number[];
  /** Ordered group-1 expression strings (e.g. "Columns" formula). Opaque. */
  expressions: string[];
};

/**
 * One named value parameter decoded from an ACDBASSOCACTION record — the REAL
 * carrier of the associative-array counts / spacings in AutoCAD-produced DXF
 * (audit §12 S-ARRAYRECT-PARAMS-DECODE).
 *
 * Reference grammar (verified against TWO independent real AutoCAD fixtures —
 * deviantollam/TastingTurntable "2018 format" + AFMD/layouts mask_layout.dxf —
 * and the ODA reference implementation `OdDbImpValueParam::dxfInFields`
 * [Teigha DbConstraints/DbImpAssocActionBody.cpp]):
 *
 *   90  <class version, 0>
 *   1   <param name>            — e.g. "Items" / "Rows" / "AxesAngle"
 *   90  <unit type>             — OdValue::UnitType (0 unitless, 1 distance,
 *                                 2 angle, ...)
 *   90  <number of values>
 *   per value: the AcDbEvalVariant group (40 real / 90 int32) + 330 input-
 *              variable id (ACDBASSOCVALUEDEPENDENCY FK; "0" when literal)
 *   330 <controlled-object dependency id>
 *
 * Each value keeps its ORIGINAL group code (40 vs 90) so the int / real
 * distinction the producer wrote is preserved — no type inference.
 */
export type AssocActionValueParam = {
  /** Parameter name (group 1) — e.g. "Items", "RowSpacing", "AxesAngle". */
  name: string;
  /** OdValue::UnitType (group 90 after the name): 0 unitless / 1 distance / 2 angle. */
  unitType?: number;
  /** Decoded values in source order, each with its original group code (40/90). */
  values: Array<{ code: number; value: number }>;
};

/**
 * Decoded ACDBASSOCVERTEXACTIONPARAM record — a NAMED point-valued geometry
 * parameter owned by an assoc action. Real AutoCAD persists the array's base
 * plane through these: the action owns vertex params named "BaseNormal" (the
 * base-plane normal — see ObjectARX
 * `AcDbAssocArrayCommonParameters::setBasePlane(basePoint, normal)` and the
 * ODA mirror `OdDbImpAssocArrayCommonParameters` writing geom params
 * "BaseNormal" / "Origin"), plus "Origin" / "basePoint".
 *
 * Grammar (verified against the real fixtures + ODA
 * `OdDbImpAssocVertexActionParam::dxfInFields`):
 *
 *   100 AcDbAssocActionParam → 90 ver [→ 90 0 when ver=1] → 1 <name>
 *   100 AcDbAssocSingleDependencyActionParam → 90 0 → 330 <dependency id>
 *   100 AcDbAssocVertexActionParam → 90 0 → 10/20/30 <point>
 *
 * These records are NOT diverted out of `rawObjectGroups` — they round-trip
 * verbatim through the raw passthrough; this type is the resolve-time typed
 * PROJECTION only (preserve-source-never-infer).
 */
export type AssocVertexActionParam = {
  /** Self handle (group 5). */
  handle: string;
  /** Owner handle (first 330 at control depth 0 — the owning assoc action). */
  ownerHandle?: string;
  /** Parameter name (group 1 on the AcDbAssocActionParam subclass). */
  name: string;
  /** The vertex point (groups 10/20/30 on the AcDbAssocVertexActionParam subclass). */
  point: { x: number; y: number; z: number };
};

/**
 * The 3D grid locator of one associative-array item — audit §12
 * S-ARRAYRECT-ITEMS-DECODE. Persisted as three group-90 int32s on the inline
 * AcDbAssocArrayItem record (ODA `OdDbImpAssocArrayItem::dxfInFields`,
 * Teigha DbConstraints/DbImpAssocArrayParameters.cpp — `OdDbItemLocator(
 * itemIndex, rowIndex, levelIndex)`). For the rectangular family `itemIndex`
 * is the COLUMN index (matches the "Items"=column-count value-param naming);
 * polar/path reuse the same structure with family-specific semantics.
 */
export type AssocArrayItemLocator = {
  /** Column index for the rectangular family (group 90 #2 on the item). */
  itemIndex: number;
  /** Row index (group 90 #3). */
  rowIndex: number;
  /** Level index — Z-stack (group 90 #4). */
  levelIndex: number;
};

/**
 * One inline AcDbAssocArrayItem record decoded from an
 * ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY — audit §12
 * S-ARRAYRECT-ITEMS-DECODE.
 *
 * Reference grammar (ODA `OdDbImpAssocArrayItem::dxfInFields` +
 * `dxfOutFields`, Teigha DbConstraints/DbImpAssocArrayParameters.cpp,
 * byte-matched against the real-AutoCAD fixtures from #303 —
 * deviantollam/TastingTurntable 2018-format + AFMD/layouts mask_layout.dxf,
 * whose items carry flags=8 translation-only records):
 *
 *   100 AcDbAssocArrayItem
 *   90  <class version, 0>
 *   90  <itemIndex> → 90 <rowIndex> → 90 <levelIndex>   (OdDbItemLocator)
 *   90  <flags>      — bit field, see below
 *   if (flags & kDefaulTransform)  16×40 <default 4×4 row-major matrix>
 *   else                           11/21/31 <translation point> (the default
 *                                  transform is pure translation —
 *                                  `m_matrix.setToTranslation(point)`)
 *   if (flags & kRealtiveTransform) 16×40 <relative 4×4 row-major matrix>
 *                                  (the per-item override — a moved/rotated
 *                                  item relative to its default grid slot;
 *                                  per-item ROTATION lives here, #303's
 *                                  itemRotation research deferral)
 *   if (!erased)                   330 <entityProviderId> — the item's
 *                                  display block-reference (soft pointer)
 *   if (flags & kModified)         330 <repBodyId> — the
 *                                  AcDbAssocArrayModifyActionBody that
 *                                  source-replaces this item (ARRAYEDIT)
 *
 * Flag bits (ODA `OdDbImpAssocArrayItem::Flags`,
 * DbImpAssocArrayParameters.h): kErased=1, kRealtiveTransform=2 [sic — ODA's
 * own spelling], kDefaulTransform=4, kItemPresent=8, kModified=16,
 * kChanged=32. The reader derives `erased = kErased | !kItemPresent`
 * (DbImpAssocArrayParameters.cpp dxfInFields) — both writers always pair the
 * two bits, so a record missing kItemPresent IS erased.
 *
 * Every field is the VERBATIM decoded source value (preserve-source-never-
 * infer): the translation-point and full-matrix default-transform forms are
 * kept DISTINCT (never converted into each other), `flags` keeps the raw
 * word, and `rawGroups` carries the item's exact group slice so anything this
 * typed view does not name (e.g. the version group) survives alongside the
 * owning body's own verbatim `rawGroups` round-trip.
 */
export type AssocArrayItem = {
  /** Grid locator (three group-90 int32s after the version). */
  locator: AssocArrayItemLocator;
  /** Verbatim flags word (group 90 #5) — see the bit table above. */
  flags: number;
  /**
   * Decoded erased state: `kErased(1) | !kItemPresent(8)` (ODA reader rule).
   * AutoCAD lets the user erase individual array items (ARRAYEDIT / grip
   * Ctrl-click + ERASE); an erased item keeps its record (so un-erase can
   * restore it) but has NO entity provider. Projected to the model so the
   * renderer COULD skip erased copies (render arm = DEFUSE S7 follow-up,
   * NOT this slice).
   */
  erased: boolean;
  /** Decoded `kModified(16)` — the item is source-replaced via `repBodyHandle`. */
  modified: boolean;
  /**
   * Translation-only default transform (groups 11/21/31) — present exactly
   * when the source lacked kDefaulTransform(4). Mutually exclusive with
   * `defaultTransform`.
   */
  position?: { x: number; y: number; z: number };
  /**
   * Full default-transform matrix (16×40, 4×4 row-major) — present exactly
   * when the source carried kDefaulTransform(4).
   */
  defaultTransform?: number[];
  /**
   * Per-item relative transform OVERRIDE (16×40, 4×4 row-major) — present
   * exactly when the source carried kRealtiveTransform(2). This is the
   * "Item overrides[]" carrier: a user-moved/rotated item persists its delta
   * here (compounded onto the default transform at evaluation — ODA
   * `OdDbImpAssocArrayItem::getTransform(bCompounded)`).
   */
  relativeTransform?: number[];
  /**
   * The item's entity provider FK (first conditional 330) — the per-item
   * display block-reference inside the array's anonymous *U block (ODA
   * `OdDbAssocArrayItem::entityProvider`; composeForLoad opens it as
   * OdDbBlockReference). Absent exactly when the item is erased. This is the
   * only PERSISTED per-item entity link — an item ↔ ORIGINAL-source-entity
   * map is NOT persisted (sources are action-level AcDbAssocDependency FKs
   * shared by every item), so `itemSourceMapping` stays a derivation-only
   * deferral (see applyAssocArrayResolution).
   */
  entityProviderHandle?: string;
  /**
   * FK to the AcDbAssocArrayModifyActionBody overriding this item (second
   * conditional 330) — present exactly when `modified` (kModified bit).
   */
  repBodyHandle?: string;
  /**
   * Verbatim group slice of this item record (from its 100 AcDbAssocArrayItem
   * marker up to the next item / the trailing body matrix). The owning body's
   * `rawGroups` is the authoritative round-trip channel; this per-item slice
   * exists so anything the typed fields do not name is still inspectable
   * per item.
   */
  rawGroups: DxfGroup[];
};

/**
 * Decoded shape of one AcDbAssoc* OBJECTS record. Mirrors
 * `DxfDynamicBlockObject` / `AssocVariable`: typed handle/owner plus the full
 * raw group list for verbatim re-emission. The decoded rectangular params and
 * dependency reference are populated only on the relevant subclasses. (Batch-8
 * S-ARRAYASSOC-PARSE.) Defined here (not in assocArrayParser) so
 * `DxfObjectsSection` references it without a types ↔ parser import cycle.
 */
export type DxfAssocArrayObject = {
  /** Self handle (group 5). */
  handle: string;
  /** Owner handle (first 330 at control depth 0). */
  ownerHandle?: string;
  /** Concrete object type (group 0) — e.g. "ACDBASSOCARRAYACTIONBODY". */
  objectType: string;
  /** Ordered list of 100-code subclass markers (round-trip + dispatch). */
  subclassMarkers: string[];
  /** Parameter family this record carries (rectangular fully decoded). */
  paramFamily: AssocArrayParamFamily;
  /** Decoded rectangular parameters — present only for the rect-params record. */
  rectParams?: AssocArrayRectParams;
  /**
   * For an ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY record:
   * the embedded parameters CLASS NAME (the first group 1 after the
   * AcDbAssocArrayActionBody subclass marker) — e.g.
   * "AcDbAssocArrayRectangularParameters". Real AutoCAD does NOT emit a
   * standalone *PARAMETERS OBJECTS record; the action body inlines the
   * parameters object with this class name so the reader can instantiate the
   * right subclass (audit §12 S-ARRAYRECT-PARAMS-DECODE; verified against the
   * real fixtures — see AssocActionValueParam above). Verbatim source string;
   * absent when the body carries no inline parameters block.
   */
  paramsClassName?: string;
  /**
   * For an ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY record:
   * the item-list CLASS NAME — the `1 <class>` group between the item count
   * and the first item record (ODA `OdDbImpAssocArrayParameters::dxfInFields`
   * reads ONE class string for the whole list, then instantiates `count`
   * items of that class; real fixtures: "AcDbAssocArrayItem"). Verbatim
   * source string; absent when the body persisted zero items (the count
   * gates the class group — audit §12 S-ARRAYRECT-ITEMS-DECODE).
   */
  itemsClassName?: string;
  /**
   * For an ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY record:
   * the inline per-item AcDbAssocArrayItem records (audit §12
   * S-ARRAYRECT-ITEMS-DECODE — grammar reference on `AssocArrayItem`).
   * Absence-preserving AND all-or-nothing: absent when the body persisted no
   * items OR when any item record deviates from the verified grammar (a
   * half-decoded list would misreport the array's population — the raw
   * stream still round-trips verbatim either way).
   */
  items?: AssocArrayItem[];
  /**
   * For an ACDBASSOCARRAYMODIFYACTIONBODY record: the grid locators of the
   * items this modify body overrides (ARRAYEDIT → Replace-Item). Grammar
   * (ODA `OdDbImpAssocArrayModifyActionBody::dxfOutFields` — the ODA DXF
   * reader side is an unimplemented stub, so this is WRITER-verified; both
   * its DWG filers persist the identical ver+count+locators structure):
   *
   *   100 AcDbAssocArrayModifyActionBody
   *   70  <version, 0>
   *   90  <count>
   *   per locator: 90 <itemIndex> → 90 <rowIndex> → 90 <levelIndex>
   *
   * No real-AutoCAD ARRAYEDIT-replace fixture was available to byte-match
   * (the #303 fixtures carry no modify bodies) — the decode is strict
   * all-or-nothing and the record round-trips verbatim via rawGroups
   * regardless.
   */
  overriddenItemLocators?: AssocArrayItemLocator[];
  /**
   * For an ACDBASSOCACTION record: the named value parameters decoded from the
   * AcDbAssocAction subclass tail (the rectangular family stores Items / Rows /
   * Levels / ItemSpacing / RowSpacing / LevelSpacing / RowElevation /
   * AxesAngle / AlignItems here — ODA `OdDbImpAssocArrayRectangularParameters`
   * + real fixtures). Absent when the action carries none (absence-preserving).
   */
  valueParams?: AssocActionValueParam[];
  /**
   * For an ACDBASSOCDEPENDENCY record: the handle of the object the dependency
   * references (the source-entity FK). Populated only on dependency records.
   */
  dependencyRefHandle?: string;
  /** Full raw group list for verbatim re-emission via the writer. */
  rawGroups: DxfGroup[];
};

export type DxfObjectsSection = {
  kind: "objects-section";
  dictionaries: DxfDictionaryEntry[];
  multileaderStyles: DxfMultileaderStyleEntry[];
  layouts: DxfLayoutEntry[];
  // Dynamic block authoring objects (Tier 1 and forward-compatible). All
  // ACDBBLOCK*/ACDBDYN*/ACDBEVALGRAPH/ACDBBLOCKREPRESENTATION records land
  // here with their full raw group list plus decoded handle + owner.
  // See packages/drawing-model/src/dxfBridge.dynamic.ts for the typed
  // bridge into DynamicBlockMetadata.
  dynamicBlockObjects: DxfDynamicBlockObject[];
  // ── Associative array graph (Batch-8 S-ARRAYASSOC-PARSE) ──
  // The AcDbAssocArray OBJECTS graph backing every associative ARRAYRECT /
  // ARRAYPOLAR / ARRAYPATH entity: ACDBASSOCNETWORK → ACDBASSOCACTION →
  // ACDBASSOCARRAYACTIONBODY → ACDBASSOCARRAYRECTANGULARPARAMETERS plus the
  // ACDBASSOCDEPENDENCY records that FK back to the source entity. Each record
  // carries its handle/owner + decoded rectangular params (rectangular case)
  // plus the full raw group list so the writer re-emits it verbatim. Pulled
  // OUT of rawObjectGroups (mirrors dynamicBlockObjects) so the section stays
  // clean and the second-pass resolver (handleResolution.resolveAssocArrays)
  // can join entity → ext-dict → action body → params + source handles.
  // Optional so legacy DXF files (no arrays) parse to a clean section.
  assocArrays?: DxfAssocArrayObject[];
  // ── Annotative scale infrastructure (PR-2) ──
  // Catalogue of `AcDbScale` records lifted out of ACAD_SCALELIST. Each
  // entry carries its handle, name, scaleFactor, isHidden flag — used by
  // the second-pass resolver (handleResolution.resolveAnnotativeContexts)
  // to populate `entity.annotativeContexts[].scaleName/scaleFactor`.
  // Optional so legacy DXF files (no annotative data at all) parse to a
  // clean section without forcing an empty array on the bridge.
  acadScales?: import("./objects/objectsAnnotativeTypes").AcDbScale[];
  // Per-scale context-data records collected during OBJECTS parsing.
  // Each entry pairs the parsed AnnotScaleContext with its raw scale-
  // handle FK; the second-pass resolver swaps the FK for a name and
  // pushes the context onto the matching entity.
  annotScaleContexts?: Array<{
    /** Handle of the context-data record itself. */
    handle: string;
    /** Handle of the parent extension dictionary (entity-side anchor). */
    ownerHandle: string;
    /** Handle of the parent AcDbScale (resolved second pass). */
    scaleHandle: string;
    /** Concrete subclass (round-trip + dispatch). */
    subclass: string;
    /** Decoded per-scale fields. scaleName/scaleFactor filled later. */
    context: import("./objects/objectsAnnotativeTypes").AnnotScaleContext;
  }>;
  // The ACAD_SCALELIST DICTIONARY itself, captured separately so the
  // writer can re-emit the dictionary entries in their original order.
  // Optional — a DXF without an ACAD_SCALELIST DICT (legacy / authored by
  // older AutoCAD versions) bootstraps from `acadScales` alone.
  acadScaleListDict?: import("./objects/objectsAnnotativeTypes").AcadScaleListDictEntry;
  // ── XCLIP / SPATIAL_FILTER (EPIC-VIEWPORT-CLIP SLICE 2) ──
  // SPATIAL_FILTER (AcDbSpatialFilter) records lifted out of the OBJECTS
  // section. Each backs an XCLIP / VPCLIP boundary on a block reference or
  // paper-space VIEWPORT: it carries the clip polygon (boundaryPoints) plus
  // the block→WCS inverse transform. SLICE 3 (`resolveViewportClip`,
  // handleResolution.ts) joins each filter to its owning entity via the
  // ext-dict `ownerHandle` and projects the boundary to a typed `clipPath`.
  // Like `acadScales`, these are pulled OUT of `rawObjectGroups` — the
  // writer reconstructs each record from this typed list. Optional so
  // legacy DXF files with no clipped references parse to a clean section.
  spatialFilters?: import("./objects/spatialFilterTypes").DxfSpatialFilter[];
  // Unrecognised OBJECTS entries round-trip verbatim so the writer can
  // re-emit them without loss (mirrors rawTableGroups in tables-section).
  rawObjectGroups: DxfGroup[];
};

/* ── BLOCKS SECTION ──────────────────────────────── */
// One BLOCK definition parsed from the DXF BLOCKS section. The entities
// inside the block (LINE, CIRCLE, TEXT, ATTDEF, INSERT, ...) are parsed
// with the same machinery as top-level ENTITIES, so `entities[]` is a
// regular list of DxfEntity. ATTDEF records inside the block preserve
// their `tag`, `defaultValue`, and handle so MLEADER block content
// renderer can link each ATTDEF to a `blockAttributeReferences[]` entry.
//
// Unknown entity types inside a block still round-trip verbatim through
// the `DxfUnsupportedEntity` branch, exactly like the top-level ENTITIES
// section does — nothing is dropped.
export type DxfBlockDefinition = {
  name: string; // group 2 — block name
  handle: string; // group 5 — block handle (may be empty)
  ownerHandle?: string; // group 330 — soft pointer to owner
  layer: string; // group 8
  flags: number; // group 70 — block-type flags bitmask
  basePoint: DxfPoint3D; // group 10/20/30 — base point
  xrefPathName: string; // group 1 — xref path (empty for non-xrefs)
  description: string; // group 4 — description (may be empty)
  // Parsed child entities. Each entity is produced by parseSupportedEntity
  // (for recognised types) or falls through to an unsupported-entity
  // record. ATTDEF entries live here as unsupported-entities because the
  // full-blown ATTDEF primitive is not yet in the supported set — but
  // their group list is preserved so MLEADER rendering can extract
  // `tag`/`prompt`/`default` strings on demand.
  entities: DxfEntity[];
  // Raw groups from the matching ENDBLK record so the writer can restore
  // the block tail byte-for-byte (handle, layer, subclass marker, xdata).
  endblkGroups: DxfGroup[];
  // T9b — the ENDBLK's OWN group-5 handle, consulted ONLY when `endblkGroups`
  // is empty (a typed / from-scratch block whose ENDBLK the writer must
  // reconstruct). R13+ requires every ENDBLK to carry a handle + a `330` owner
  // (the same BLOCK_RECORD the BLOCK is owned by — ezdxf `BlockLayout`); a
  // handle-less ENDBLK is silently re-minted by ezdxf and rejected by AutoCAD.
  endblkHandle?: string;
};

export type DxfBlocksSection = {
  kind: "blocks-section";
  blocks: DxfBlockDefinition[];
  // Groups appearing outside any BLOCK/ENDBLK record — AutoCAD does not
  // normally put anything here but we preserve it verbatim so the writer
  // can round-trip unusual files without losing data.
  rawBlockGroups: DxfGroup[];
};

export type DxfSection =
  | DxfRawSection
  | DxfEntitiesSection
  | DxfTablesSection
  | DxfObjectsSection
  | DxfBlocksSection;

export type DxfDocument = {
  sections: DxfSection[];
};

export function point3D(x = 0, y = 0, z = 0): DxfPoint3D {
  return { x, y, z };
}

export function clonePoint(point: DxfPoint3D): DxfPoint3D {
  return {
    x: point.x,
    y: point.y,
    z: point.z,
  };
}

const SUPPORTED_ENTITY_TYPES: ReadonlySet<string> =
  new Set<DxfSupportedEntityType>([
    "LINE",
    "POINT",
    "CIRCLE",
    "ARC",
    "ELLIPSE",
    "SPLINE",
    "LWPOLYLINE",
    "TEXT",
    "MTEXT",
    "DIMENSION",
    "MLEADER",
    "REVCLOUD",
    "WIPEOUT",
    "HATCH",
    "INSERT",
    "ARRAYRECT",
    "VIEWPORT",
    "ATTDEF",
    "ATTRIB",
    "SEQEND",
    "POLYLINE",
    "VERTEX",
    "ACAD_TABLE",
    "OLE2FRAME",
  ]);

// DXF entity-type aliases. AutoCAD writes both "MLEADER" and "MULTILEADER"
// as entity-type (group code 0) depending on version / workflow — real
// production files observed both. Normalize to the canonical name so
// downstream consumers only handle the one spelling.
const ENTITY_TYPE_ALIASES: ReadonlyMap<string, DxfSupportedEntityType> =
  new Map<string, DxfSupportedEntityType>([["MULTILEADER", "MLEADER"]]);

export function isSupportedEntityType(
  value: string,
): value is DxfSupportedEntityType {
  return (
    SUPPORTED_ENTITY_TYPES.has(value) || ENTITY_TYPE_ALIASES.has(value)
  );
}

/**
 * Normalize a DXF entity-type string from the raw file to the canonical
 * DxfSupportedEntityType spelling (e.g. "MULTILEADER" → "MLEADER").
 * Returns the input unchanged if it's already canonical.
 */
export function normalizeEntityType(value: string): string {
  return ENTITY_TYPE_ALIASES.get(value) ?? value;
}

export function createDxfDocument(entities: DxfEntity[]): DxfDocument {
  return {
    sections: [
      {
        kind: "entities-section",
        entities,
      },
    ],
  };
}
